import { useCustomSource, useLayerStore } from '@uofu-uss-gis/ui-react';
import { useEffect, useMemo, useRef } from 'react';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import {
  EVENT_PARKING_LAYER_CONFIG,
  EventParkingLayerConfig,
  buildEventParkingServiceUrl,
  getEventSearchMapLayerId,
} from '../config/eventParkingConfig';
import {
  buildEventParkingSearchVariants,
  buildEventTypeWhere,
  buildLikeAnyWhere,
} from '../utils/eventParkingSearchUtils';

const aliasTableFL = new FeatureLayer({ url: `${buildEventParkingServiceUrl()}/14` });

/**
 * Always-mounted single source for event parking search.
 * Registered before mapView initializes so the SearchWidget captures it at startup.
 * Active event config and FeatureLayer are tracked via refs and read at call time.
 */
export function EventParkingSearchSources() {
  const layers = useLayerStore((state) => state.layers);

  const activeConfigRef = useRef<EventParkingLayerConfig | null>(null);
  const eventFLRef = useRef<FeatureLayer | null>(null);

  // Derive once per render — EVENT_PARKING_LAYER_CONFIG is a constant so find returns
  // the same object reference as long as the same event is active.
  const activeConfig = EVENT_PARKING_LAYER_CONFIG.find((config) => layers[config.layerId]) ?? null;
  const activeSearchMapLayerId = activeConfig ? getEventSearchMapLayerId(activeConfig) : undefined;

  // Keep refs in sync so async callbacks always see the current active config/layer
  useEffect(() => {
    activeConfigRef.current = activeConfig;
    if (activeConfig) {
      const primaryServiceIdx = Array.isArray(activeConfig.serviceLayerIndex)
        ? activeConfig.serviceLayerIndex[0]
        : activeConfig.serviceLayerIndex;
      const idx = activeConfig.searchLayerIndex ?? primaryServiceIdx;
      eventFLRef.current = new FeatureLayer({
        url: `${buildEventParkingServiceUrl()}/${idx}`,
      });
    } else {
      eventFLRef.current = null;
    }
  }, [activeConfig]);

  // Source is recreated when the active event changes so the store gets the updated layerId.
  // getSuggestions/getResults read from refs — the original function reference captured by
  // SearchWidget at mapView init still works correctly because refs are always current.
  const source = useMemo(
    () => ({
      sourceIndex: 8,
      name: 'Event Parking',
      placeholder: 'Search event parking',
      maxResults: 50,
      maxSuggestions: 50,
      minSuggestCharacters: 1,
      // layerId lets performSearch find the map layer and query by OBJECTID (the key we return)
      layerId: activeSearchMapLayerId,

      getSuggestions: async ({ suggestTerm }: { suggestTerm: string }) => {
        const config = activeConfigRef.current;
        const eventFL = eventFLRef.current;
        if (!config || !eventFL) return [];
        try {
          const searchVariants = buildEventParkingSearchVariants(suggestTerm);
          if (searchVariants.length === 0) return [];

          const eventTypeWhere = buildEventTypeWhere(config.aliasEventType);
          const aliasWhere = `(${buildLikeAnyWhere('UPPER(alias)', searchVariants)}) AND ${eventTypeWhere}`;
          const lotWhere = buildLikeAnyWhere('UPPER(LOTNUMBER)', searchVariants);

          const aliasResults = await aliasTableFL.queryFeatures({
            where: aliasWhere,
            outFields: ['alias_id', 'alias'],
            returnGeometry: false,
            num: 50,
          });

          const aliases = aliasResults.features ?? [];
          const aliasIds = aliases.map((f) => f.attributes.alias_id as number);
          const aliasTextMap = new Map<number, string>(
            aliases.map((f) => [f.attributes.alias_id as number, f.attributes.alias as string])
          );

          const directFeatureResults = await eventFL.queryFeatures({
            where: lotWhere,
            outFields: ['OBJECTID', 'LOT_ALID', 'LOTNUMBER'],
            returnGeometry: false,
            num: 50,
          });

          const aliasFeatureResults = aliasIds.length > 0
            ? await eventFL.queryFeatures({
                where: `LOT_ALID IN (${aliasIds.join(', ')})`,
                outFields: ['OBJECTID', 'LOT_ALID', 'LOTNUMBER'],
                returnGeometry: false,
                num: 50,
              })
            : null;

          const allFeatures = [
            ...(aliasFeatureResults?.features ?? []),
            ...(directFeatureResults.features ?? []),
          ];

          if (allFeatures.length === 0) {
            return [];
          }

          const unresolvedAliasIds = Array.from(
            new Set(
              allFeatures
                .map((feature) => feature.attributes.LOT_ALID as number | null | undefined)
                .filter((aliasId): aliasId is number => aliasId != null && !aliasTextMap.has(aliasId))
            )
          );

          if (unresolvedAliasIds.length > 0) {
            const missingAliasResults = await aliasTableFL.queryFeatures({
              where: `(alias_id IN (${unresolvedAliasIds.join(', ')})) AND ${eventTypeWhere}`,
              outFields: ['alias_id', 'alias'],
              returnGeometry: false,
              num: unresolvedAliasIds.length,
            });

            (missingAliasResults.features ?? []).forEach((feature) => {
              aliasTextMap.set(feature.attributes.alias_id as number, feature.attributes.alias as string);
            });
          }

          const seen = new Set<string>();
          return allFeatures.flatMap((feature) => {
            const aliasText = aliasTextMap.get(feature.attributes.LOT_ALID as number) ?? '';
            const lotNumber = feature.attributes.LOTNUMBER as string;
            const label = aliasText ? `${aliasText} - ${lotNumber}` : lotNumber;
            const dedupeKey = `${aliasText}|${lotNumber}`;

            if (seen.has(dedupeKey)) {
              return [];
            }

            seen.add(dedupeKey);
            return [{ text: label, key: String(feature.attributes.OBJECTID), magicKey: String(feature.attributes.OBJECTID) }];
          });
        } catch (err) {
          console.error('[EventSearch] getSuggestions error:', err);
          return [];
        }
      },
    }),
    // Recreate when active event changes so store has current layerId for performSearch
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeSearchMapLayerId]
  );

  useCustomSource(source, { priority: 1 });
  return null;
}

